import { State, MainThunkResult, MainThunkDispatch } from "../../../Reducers"
import { failMessage, successMessage } from "../../Client/MessagesActions"
import { DataAPIType } from "../../Types"
import { APIDataModel, getDataUrl } from "../../../Models/API/DataModel"
import { safePromise } from "../../../Helpers/Functions/promise"
import { apiFetch } from "../../Client/NetworkActions"
import { apiEndpoints } from "../../../Routes/api"
import {
    downloadAsCSV,
    downloadAsJSON
} from "../../../Helpers/Functions/downloadAs"
import { SortExpression } from "../../../Models/Client/SortExpression"
import { DataAccess } from "../../../Models/Client/DataAccess"
import { Result } from "../../../Models/Utility/Result"
import { asError } from "../../../Helpers/Functions/error"
import { DataAction } from "../../../Reducers/API/Data/DataReducer"
import { reportError } from "../../../Helpers/Functions/sentry"
import { metaAIClient, metaAI } from "neql"
import { getApiKey } from "../../../Helpers/Functions/Selectors/auth"

const DEFAULT_DATASET_NAME = "default"

interface UploadDataOptions {
    overwriteIfExists?: boolean
    onProgress?: (progress: number) => void
}

export const uploadData =
    (
        file: Blob,
        objectName: string,
        { overwriteIfExists, onProgress }: UploadDataOptions = {}
    ) =>
    async (dispatch: MainThunkDispatch): Promise<string> => {
        // Note:
        // Because uploading to the top level directory will
        // cause problem when we introduce the concept of "dataset",
        // I upload everything to the "default" folder for now.
        const uploadPath = `${DEFAULT_DATASET_NAME}/${objectName}`
        const endpoint = apiEndpoints.getUploadDataUrl({
            uploadPath,
            fileType: file.type
        })
        const data = await dispatch(
            apiFetch(`GET_UPLOAD_DATA_URL_${objectName}`, endpoint, {
                method: overwriteIfExists ? "PUT" : "POST"
            })
        )

        if (data === undefined) {
            throw new Error("Error uploading data.")
        }

        await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()

            xhr.addEventListener("load", resolve)
            xhr.upload.addEventListener("abort", reject)
            xhr.upload.addEventListener("error", reject)
            if (onProgress) {
                xhr.upload.addEventListener("progress", ({ loaded, total }) =>
                    onProgress(loaded / total)
                )
            }
            xhr.open("PUT", data.uploadUrl)
            xhr.send(file)
        })

        return getDataUrl(data)
    }

const destroyOne =
    (path: string) =>
    async (dispatch: MainThunkDispatch): Promise<any> => {
        const endpoint = apiEndpoints.deleteData({ path })
        const { error } = await safePromise(
            dispatch(
                apiFetch(`DELETE_DATA_${path}`, endpoint, {
                    method: "DELETE"
                })
            )
        )

        if (error !== undefined) throw error
    }

const getMetaAISignedUrl =
    (path: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const matched = path.match(/^ai\/([^/]+)\/([^/]+)\//)
        const instanceId =
            matched === null ? Number.NaN : Number.parseInt(matched[2], 10)
        const apiKey = getApiKey(getState())

        if (matched === null || Number.isNaN(instanceId)) {
            throw Error(`malformed path: ${path}`)
        }

        if (apiKey === undefined) {
            throw Error("API key is missing")
        }

        const client = metaAIClient(apiKey)

        if (client === undefined) {
            throw Error("Meta AI service not available.")
        }

        const { data } = await client.query<
            metaAI.GetSignedUrlQuery,
            metaAI.GetSignedUrlQueryVariables
        >({
            query: metaAI.GetSignedUrlDocument,
            variables: {
                url: `data://${path}`,
                instanceId,
                predictionId: matched[1]
            }
        })

        if (data.resolve_data_ref?.url !== undefined) {
            dispatch({
                type: DataAPIType.signedUrlRequestStateChanged,
                meta: { path },
                payload: Result.ok(data.resolve_data_ref.url)
            })
        }
    }

export const getSignedUrl =
    (path: string, access: DataAccess) =>
    async (dispatch: MainThunkDispatch) => {
        const action: DataAction = {
            type: DataAPIType.signedUrlRequestStateChanged,
            meta: { path },
            payload: "loading"
        }
        const endpoint = apiEndpoints.getDataSignedUrl({
            path,
            jobId: access.type === "perJob" ? access.jobID : undefined,
            baselineId: access.type === "perGT" ? access.baselineID : undefined,
            taskId: access.type === "perTask" ? access.taskID : undefined,
            collabTaskId:
                access.type === "perCollabTask"
                    ? access.collabTaskId
                    : undefined
        })

        try {
            if (path.startsWith("ai/")) {
                await dispatch(getMetaAISignedUrl(path))
                return
            }

            const data = await dispatch(
                apiFetch<{ signedUrl: string }>(
                    `GET_DATA_SIGNED_URL_${path}`,
                    endpoint
                )
            )

            if (data !== undefined) {
                dispatch({ ...action, payload: Result.ok(data.signedUrl) })
                return data.signedUrl
            }
        } catch (e) {
            reportError(e)
            dispatch({
                ...action,
                payload: Result.error<string>(asError(e).message)
            })
        }
    }

export const destroy =
    (paths: string[]): MainThunkResult<Promise<any>> =>
    (dispatch: MainThunkDispatch) => {
        return paths
            .reduce(
                (passed, path) => passed.then(() => dispatch(destroyOne(path))),
                Promise.resolve()
            )
            .then(() =>
                dispatch(
                    successMessage(
                        paths.length > 1
                            ? "Files are deleted successfully"
                            : "File is deleted successfully"
                    )
                )
            )
            .then(_ => dispatch(getDataList(0)))
            .catch(e => failMessage("Could not delete all data", e))
    }

export const getDataList =
    (
        page: number,
        maybeSort?: SortExpression<APIDataModel>
    ): MainThunkResult<Promise<any>> =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const state = getState()
        const sort = maybeSort || state.api.data.sort
        const pageSize = state.api.data.pageSize
        const endpoint = apiEndpoints.getDataList({
            page,
            pageSize,
            sort,
            includeSignedUrl: true
        })
        const requestID = `GET_DATA_LIST_${JSON.stringify({
            page,
            pageSize,
            sort
        })}`

        dispatch({
            type: DataAPIType.requestList
        })

        const { error, data } = await safePromise(
            dispatch(apiFetch(requestID, endpoint))
        )

        if (error !== undefined) {
            return
        }

        if (data === undefined) {
            // request is throttled.
            // need to dispatch receiveList event to set loading to false
            dispatch({
                type: DataAPIType.receivedList,
                payload: {
                    list: state.api.data.list,
                    pages: state.api.data.pages,
                    pageNumber: page,
                    totalItemCount: state.api.data.totalItemCount,
                    sort
                }
            })
            return
        }

        dispatch({
            type: DataAPIType.receivedList,
            payload: {
                list: data.content,
                pages: data.totalPages,
                pageNumber: page,
                totalItemCount: data.totalElements,
                sort
            }
        })
    }

export const downloadAllDataUrls =
    (fileName: string, type: "csv" | "json"): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const state = getState()
        const sort = state.api.data.sort
        const pages = Math.ceil(state.api.data.totalItemCount / 1000)
        const urls = []

        for (let i = 0; i < pages; i++) {
            const endpoint = apiEndpoints.getDataList({
                sort,
                page: i,
                pageSize: 1000,
                includeSignedUrl: false
            })
            const requestID = `GET_ALL_DATA_LIST_${JSON.stringify(sort)}`

            const { error, data } = await safePromise(
                dispatch(apiFetch(requestID, endpoint, undefined, 1))
            )

            if (error !== undefined || data === undefined) {
                return
            }

            urls.push(...data.content.map(getDataUrl))
        }

        if (type === "json") {
            downloadAsJSON(urls, fileName)
        } else {
            downloadAsCSV(urls.join("\n"), fileName)
        }
    }
